import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ChevronLeft,
  Download,
  Smartphone,
  ShieldCheck,
  Zap,
  Bell,
  CheckCircle2,
  Copy,
  QrCode,
  UserCheck,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { copyToClipboard } from "../lib/clipboard";
import { SiteHeader } from "../components/site/SiteHeader";
import { MobileBottomBar } from "../components/site/MobileBottomBar";

const DEFAULT_PLAYER_APK = "/downloads/maxlive88-app.apk";
const DEFAULT_AGENT_APK = "/downloads/maxlive88-agent.apk";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function resolveApkUrl(raw, fallback) {
  const v = (raw || "").trim();
  if (!v) return fallback;
  return v;
}

export default function DownloadAppPage() {
  const navigate = useNavigate();
  const [origin, setOrigin] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [activeTab, setActiveTab] = useState("player"); // 'player' | 'agent'
  const [cfg, setCfg] = useState({
    apk_player_enabled: true,
    apk_player_url: "",
    apk_player_version: "1.0",
    apk_player_size_kb: 50,
    apk_player_label: "Maxlive88 App",
    apk_agent_enabled: true,
    apk_agent_url: "",
    apk_agent_version: "1.0",
    apk_agent_size_kb: 50,
    apk_agent_label: "Maxlive88 Agent",
  });

  useEffect(() => {
    if (typeof window !== "undefined") setOrigin(window.location.origin);
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/public/site-settings`);
        setCfg((prev) => ({ ...prev, ...data }));
      } catch (e) {
        // fall back to defaults silently
      }
    })();
  }, []);

  // If active tab is disabled by admin, auto-switch
  useEffect(() => {
    if (activeTab === "player" && cfg.apk_player_enabled === false && cfg.apk_agent_enabled !== false) {
      setActiveTab("agent");
    } else if (activeTab === "agent" && cfg.apk_agent_enabled === false && cfg.apk_player_enabled !== false) {
      setActiveTab("player");
    }
  }, [cfg.apk_player_enabled, cfg.apk_agent_enabled, activeTab]);

  const isAgent = activeTab === "agent";
  const rawUrl = isAgent ? cfg.apk_agent_url : cfg.apk_player_url;
  const fallback = isAgent ? DEFAULT_AGENT_APK : DEFAULT_PLAYER_APK;
  const apkPath = resolveApkUrl(rawUrl, fallback);
  const isAbsolute = /^https?:\/\//i.test(apkPath);
  const fullUrl = isAbsolute ? apkPath : `${origin}${apkPath}`;
  const filename = isAgent ? "maxlive88-agent.apk" : "maxlive88-app.apk";
  const appLabel = isAgent ? cfg.apk_agent_label || "Maxlive88 Agent" : cfg.apk_player_label || "Maxlive88 App";
  const appVersion = isAgent ? cfg.apk_agent_version || "1.0" : cfg.apk_player_version || "1.0";
  const appSize = isAgent ? cfg.apk_agent_size_kb ?? 50 : cfg.apk_player_size_kb ?? 50;
  // Display size — show MB when >= 1024 KB, else show KB.
  const formatSize = (kb) => {
    const n = Number(kb) || 0;
    if (n >= 1024) return `${(n / 1024).toFixed(2)} MB`;
    return `${n} KB`;
  };
  const appSizeLabel = formatSize(appSize);
  const currentEnabled = isAgent ? cfg.apk_agent_enabled !== false : cfg.apk_player_enabled !== false;
  const bothDisabled = cfg.apk_player_enabled === false && cfg.apk_agent_enabled === false;

  const startDownload = () => {
    try {
      setDownloading(true);
      const a = document.createElement("a");
      a.href = apkPath;
      a.download = filename;
      a.rel = "noopener";
      if (isAbsolute) a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Download started — check your notifications");
    } catch (e) {
      toast.error("Could not start download");
    } finally {
      setTimeout(() => setDownloading(false), 1500);
    }
  };

  const copyLink = async () => {
    const ok = await copyToClipboard(fullUrl);
    if (ok) toast.success("Link copied");
    else toast.error("Could not copy");
  };

  const features = [
    { Icon: Zap, label: "Lightning fast", sub: "Native WebView wrapper — feels instant" },
    { Icon: Bell, label: "Push-ready", sub: "Deposit & withdrawal alerts on your lock screen" },
    { Icon: ShieldCheck, label: "Secure TLS", sub: "Same end-to-end encryption as the website" },
    { Icon: Smartphone, label: "Android 5.0+", sub: "Works on nearly all devices (21 MB install)" },
  ];

  const steps = [
    { n: 1, title: "Tap Download", body: "Press the green button — the .apk file saves to your Downloads folder." },
    { n: 2, title: "Allow install from browser", body: "If Android asks, toggle 'Allow from this source' for Chrome/your browser." },
    { n: 3, title: "Open the file", body: "Tap the downloaded .apk to install. Your data stays signed in if you used the site before." },
    { n: 4, title: "Log in", body: "Use your existing username & password — no re-registration needed." },
  ];

  return (
    <div className="min-h-screen bg-[#0a1119] text-white pb-24 lg:pb-8" data-testid="download-app-page">
      <div className="hidden lg:block">
        <SiteHeader />
      </div>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 bg-[#0a1119] border-b border-white/5">
        <div className="flex items-center gap-3 px-4 h-14">
          <button
            type="button"
            data-testid="download-back"
            onClick={() => navigate(-1)}
            className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 border border-white/10 text-white/80"
            aria-label="back"
          >
            <ChevronLeft size={18} />
          </button>
          <h1 className="text-lg font-extrabold tracking-tight">Download App</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 lg:py-8 space-y-5">
        {/* Hero card */}
        <div
          className="relative overflow-hidden rounded-3xl"
          style={{
            background:
              "linear-gradient(160deg, #0e1a2b 0%, #0b1320 50%, #13251a 100%)",
            border: "1px solid rgba(25,165,92,0.25)",
            boxShadow:
              "0 30px 80px -40px rgba(25,165,92,0.55), 0 1px 0 rgba(255,255,255,0.04) inset",
          }}
        >
          {/* ambient glow */}
          <div
            className="absolute inset-0 pointer-events-none opacity-60"
            style={{
              backgroundImage:
                "radial-gradient(circle at 10% 10%, rgba(25,165,92,0.22), transparent 45%), radial-gradient(circle at 95% 90%, rgba(245,158,11,0.16), transparent 45%)",
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.05]"
            style={{
              backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }}
          />

          <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            {/* App icon */}
            <div
              className="h-20 w-20 sm:h-24 sm:w-24 rounded-[22px] grid place-items-center shrink-0 relative"
              style={{
                background: "linear-gradient(135deg, #19a55c 0%, #0f7a40 100%)",
                boxShadow:
                  "0 18px 40px -18px rgba(25,165,92,0.7), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              <span className="font-black text-[26px] sm:text-[30px] tracking-tight text-white drop-shadow-sm">
                M88
              </span>
              <span className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-amber-500 border-2 border-[#0e1a2b] grid place-items-center">
                <Smartphone size={12} className="text-[#1a1208]" strokeWidth={3} />
              </span>
            </div>

            <div className="min-w-0 flex-1">
              <div className="text-white text-[22px] sm:text-[26px] font-extrabold leading-tight tracking-tight">
                {appLabel}
              </div>
              <div className="text-white/60 text-sm mt-1">
                Android · v{appVersion} · {appSizeLabel} installer · Official build
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  data-testid="download-apk-btn"
                  onClick={startDownload}
                  disabled={downloading || !currentEnabled}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-xl font-extrabold text-[14.5px] text-[#0a1119] disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98] transition"
                  style={{
                    background: "linear-gradient(180deg, #2fd07d 0%, #19a55c 100%)",
                    boxShadow:
                      "0 10px 24px -10px rgba(25,165,92,0.8), inset 0 1px 0 rgba(255,255,255,0.35)",
                  }}
                >
                  <Download size={17} strokeWidth={2.5} />
                  {!currentEnabled
                    ? "Disabled by admin"
                    : downloading
                    ? "Starting…"
                    : "Download APK"}
                </button>

                <button
                  type="button"
                  data-testid="download-qr-btn"
                  onClick={() => setShowQR((v) => !v)}
                  className="inline-flex items-center gap-1.5 h-11 px-3 rounded-xl font-bold text-[12.5px] text-white/90 bg-white/5 border border-white/10 hover:bg-white/10 transition"
                >
                  <QrCode size={15} />
                  {showQR ? "Hide QR" : "Scan on mobile"}
                </button>

                <button
                  type="button"
                  data-testid="download-copy-btn"
                  onClick={copyLink}
                  className="inline-flex items-center gap-1.5 h-11 px-3 rounded-xl font-bold text-[12.5px] text-white/90 bg-white/5 border border-white/10 hover:bg-white/10 transition"
                >
                  <Copy size={14} />
                  Copy link
                </button>
              </div>
            </div>
          </div>

          {/* QR expanded */}
          {showQR && fullUrl && (
            <div className="relative border-t border-white/5 p-5 sm:p-6 flex items-center gap-5">
              <div className="bg-white rounded-xl p-3 shrink-0">
                <QRCodeSVG value={fullUrl} size={128} bgColor="#ffffff" fgColor="#0a1119" level="M" />
              </div>
              <div className="text-[13px] text-white/75 leading-relaxed">
                <div className="font-bold text-white mb-1">Open your phone camera</div>
                Point it at this QR code to start the download on your mobile device. No typing, no sharing — just scan and tap the notification.
              </div>
            </div>
          )}
        </div>

        {/* Tabs — Player / Agent (filtered by admin enabled flags) */}
        {!bothDisabled && (
          <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10">
            {[
              { id: "player", label: "Player App", Icon: Smartphone, enabled: cfg.apk_player_enabled !== false },
              { id: "agent", label: "Agent App", Icon: UserCheck, enabled: cfg.apk_agent_enabled !== false },
            ]
              .filter((t) => t.enabled)
              .map((t) => {
                const active = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`tab-${t.id}`}
                    onClick={() => setActiveTab(t.id)}
                    className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-[12.5px] font-bold tracking-wide transition ${
                      active
                        ? "bg-[#19a55c] text-[#0a1119] shadow-[0_6px_18px_-8px_rgba(25,165,92,0.9)]"
                        : "text-white/70 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <t.Icon size={14} />
                    {t.label}
                  </button>
                );
              })}
          </div>
        )}

        {bothDisabled && (
          <div className="rounded-2xl bg-rose-500/5 border border-rose-500/25 p-5 text-rose-200 text-sm" data-testid="apk-disabled-banner">
            <div className="font-bold mb-1">Downloads currently unavailable</div>
            Our Android apps are temporarily disabled for maintenance. Please check back soon or use the website directly.
          </div>
        )}

        {/* Features grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {features.map((f, i) => {
            const Icon = f.Icon;
            return (
              <div
                key={i}
                className="rounded-2xl bg-[#101824] border border-white/5 p-4 flex items-start gap-3"
              >
                <div className="h-9 w-9 rounded-lg bg-emerald-500/12 border border-emerald-500/25 grid place-items-center shrink-0">
                  <Icon size={16} className="text-emerald-300" strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <div className="text-white font-bold text-[14px] leading-tight">{f.label}</div>
                  <div className="text-white/55 text-[12px] mt-0.5 leading-snug">{f.sub}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Install steps */}
        <div className="rounded-2xl bg-[#101824] border border-white/5 overflow-hidden">
          <div className="px-5 py-3.5 border-b border-white/5 text-[11.5px] text-white/55 font-bold uppercase tracking-[1.5px] flex items-center gap-2">
            <CheckCircle2 size={13} className="text-emerald-400" />
            How to install
          </div>
          <ul className="divide-y divide-white/5">
            {steps.map((s) => (
              <li key={s.n} className="px-5 py-4 flex items-start gap-3.5">
                <span
                  className="h-7 w-7 rounded-full grid place-items-center shrink-0 font-black text-[12.5px] text-[#0a1119]"
                  style={{
                    background: "linear-gradient(135deg,#2fd07d 0%,#19a55c 100%)",
                  }}
                >
                  {s.n}
                </span>
                <div className="min-w-0">
                  <div className="text-white font-bold text-[14px] leading-tight">{s.title}</div>
                  <div className="text-white/60 text-[12.5px] mt-1 leading-relaxed">{s.body}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Safety note */}
        <div
          className="rounded-2xl p-4 flex items-start gap-3"
          style={{
            background: "rgba(245,158,11,0.06)",
            border: "1px solid rgba(245,158,11,0.25)",
          }}
        >
          <ShieldCheck size={18} className="text-amber-300 shrink-0 mt-0.5" />
          <div className="text-[12.5px] text-amber-100/85 leading-relaxed">
            <span className="font-bold text-amber-200">Only install from this page.</span> We never
            distribute Maxlive88 on third-party APK sites. The installer is signed by
            Maxlive88 — your device will show the signature before install.
          </div>
        </div>
      </div>

      <div className="lg:hidden">
        <MobileBottomBar onMenuClick={() => navigate("/")} />
      </div>
    </div>
  );
}
